import { FC, memo, useCallback, useEffect } from 'react';

import { setPreventUnload } from 'flow/actions/app-actions';
import { showImagePreview } from 'flow/actions/chat-actions';
import Icon from 'flow/components/ui-components/Icon';
import IconButton from 'flow/components/ui-components/IconButton';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch } from 'flow/hooks/useRedux';

interface Props {
    imageSrc: string;
}

const LightBox: FC<Props> = ({ imageSrc }) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();

    const handleOnKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                dispatch(showImagePreview(null));
            }
        },
        [dispatch],
    );

    useEffect(() => {
        window.addEventListener('keydown', handleOnKeyDown, false);

        return () => {
            window.removeEventListener('keydown', handleOnKeyDown, false);
        };
    }, [handleOnKeyDown]);

    const handleCloseClick = useCallback(() => {
        dispatch(showImagePreview(null));
    }, [dispatch]);

    const handleContainerClick = useCallback(
        (e: React.MouseEvent) => {
            if (e.target === e.currentTarget) {
                dispatch(showImagePreview(null));
            }
        },
        [dispatch],
    );

    return (
        <div className="no-dark absolute inset-0 z-40 bg-black/70 backdrop-blur-sm">
            <button
                // The button covers the screen, so its focus ring goes inside.
                className="focus-ring-inset relative size-full cursor-pointer overflow-hidden border-none bg-transparent p-0"
                onClick={handleContainerClick}
                aria-label="Close lightbox"
                type="button"
            >
                <img
                    className="absolute top-1/2 left-1/2 inline-block h-auto max-h-full w-auto max-w-full -translate-x-1/2 -translate-y-1/2 transform"
                    src={imageSrc}
                    alt="Preview"
                />
            </button>
            <div className="absolute end-0 top-0 flex h-15 w-full justify-end bg-gray-800/70">
                <a
                    className="self-center"
                    href={imageSrc}
                    download={true}
                    onClick={() => setPreventUnload(true)}
                >
                    <IconButton
                        className="size-6 self-center"
                        tooltipLabel={i18next.t('Tooltip.Audio.Download')}
                    >
                        <Icon
                            iconKey="download"
                            className="size-4.5 text-white"
                        />
                    </IconButton>
                </a>
                <IconButton
                    onClick={handleCloseClick}
                    className="ms-3 me-3.75 size-6 self-center"
                    tooltipLabel={i18next.t('Tooltip.Dialog.Close')}
                >
                    <Icon iconKey="close" className="size-4.5 text-white" />
                </IconButton>
            </div>
        </div>
    );
};

export default memo(LightBox);
